import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp } from 'lucide-react';
import api from '@/lib/api';

const fmtEuro = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);

/**
 * Graphe mensuel (12 derniers mois) des commissions parrainage d'un parrain,
 * empilées par distance à la chaîne : Direct, +1 cran (N+1), +2 crans (N+2).
 * Vue synthétique pour comprendre où le parrain apporte le plus.
 */
export function ParrainMonthlyChart({ userId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get(`/users/${userId}/parrain-timeline`)
            .then(res => { if (!cancelled) setData(res.data); })
            .catch(() => { if (!cancelled) setData({ timeline: [], totals: { direct: 0, n1: 0, n2: 0, total: 0 } }); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [userId]);

    if (loading) {
        return (
            <Card data-testid="parrain-monthly-chart-loading">
                <CardContent className="pt-6 text-sm text-slate-400">Chargement des commissions parrainage…</CardContent>
            </Card>
        );
    }

    const timeline = data?.timeline || [];
    const totals = data?.totals || { direct: 0, n1: 0, n2: 0, total: 0 };
    const nothing = totals.total === 0;

    return (
        <Card className="border-indigo-100" data-testid="parrain-monthly-chart">
            <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-indigo-600" /> Commissions parrainage — 12 derniers mois
                </CardTitle>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-2.5 py-1.5">
                        <p className="text-[10px] uppercase font-semibold text-emerald-700">Direct</p>
                        <p className="text-sm font-bold text-emerald-900 tabular-nums">{fmtEuro(totals.direct)}</p>
                    </div>
                    <div className="rounded-lg border border-indigo-100 bg-indigo-50 px-2.5 py-1.5">
                        <p className="text-[10px] uppercase font-semibold text-indigo-700">+1 cran</p>
                        <p className="text-sm font-bold text-indigo-900 tabular-nums">{fmtEuro(totals.n1)}</p>
                    </div>
                    <div className="rounded-lg border border-purple-100 bg-purple-50 px-2.5 py-1.5">
                        <p className="text-[10px] uppercase font-semibold text-purple-700">+2 crans</p>
                        <p className="text-sm font-bold text-purple-900 tabular-nums">{fmtEuro(totals.n2)}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5">
                        <p className="text-[10px] uppercase font-semibold text-slate-600">Total 12 mois</p>
                        <p className="text-sm font-bold text-slate-900 tabular-nums">{fmtEuro(totals.total)}</p>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {nothing ? (
                    <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/40 px-4 py-10 text-center text-sm text-slate-500">
                        Aucune commission parrainage sur les 12 derniers mois.
                    </div>
                ) : (
                    <div className="h-64 w-full" data-testid="parrain-monthly-chart-canvas">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={timeline} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} />
                                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : v} />
                                <Tooltip
                                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                                    formatter={(v) => fmtEuro(v)}
                                />
                                <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="direct" stackId="a" fill="#10b981" name="Direct" radius={[0, 0, 0, 0]} />
                                <Bar dataKey="n1" stackId="a" fill="#6366f1" name="+1 cran" radius={[0, 0, 0, 0]} />
                                <Bar dataKey="n2" stackId="a" fill="#a855f7" name="+2 crans" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default ParrainMonthlyChart;
